<template>
  <v-row class="flex-nowrap" no-gutters>
    <v-col cols="5">
      <v-tooltip location="bottom center" origin="auto">
        <template #activator="{ props }">
          <v-sheet v-bind="props" class="text-caption pa-0 ma-0" :color="raiseColor">
            <v-row no-gutters>
              <v-col style="text-align: center; padding-left: 2px">
                <v-icon v-if="raiseChangeIcon" class="mb-1" :icon="raiseChangeIcon" />
                <v-icon v-else class="mb-1" icon="mdi-unfold-less-vertical" color="transparent" />
              </v-col>
              <v-col>
                <a :href="trendLink('raise')" target="_blank">
                  {{ raisePrice }}
                </a>
              </v-col>
              <v-col style="text-align: center; padding-right: 2px">
                <v-icon v-if="flags('raise', market)" size="12" icon="mdi-alert" />
                <v-icon v-else icon="mdi-unfold-less-vertical" size="12" color="transparent" />
              </v-col>
            </v-row>
          </v-sheet>
        </template>
        <span class="font-weight-bold">{{ name }} Raise {{ market }} price</span><br />
        <span v-if="flags('raise', market)">{{ flags('raise', market) }}<br /></span>
        <span>Current: {{ raisePrice }}</span
        ><br />
        <span>Previous: {{ previousRaisePrice }}</span
        ><br />
        <span>Change: {{ deltaRaisePrice }} ({{ percentRaiseChange.toFixed(2) }}%)</span>
      </v-tooltip>
    </v-col>

    <v-col class="text-caption pa-0 ma-0">
      {{ market }}
    </v-col>

    <v-col cols="5">
      <v-tooltip location="bottom center" origin="auto">
        <template #activator="{ props }">
          <v-sheet v-bind="props" class="text-caption pa-0 ma-0" :color="lowerColor">
            <v-row no-gutters>
              <v-col style="text-align: center; padding-left: 2px">
                <v-icon v-if="flags('lower', market)" size="12" icon="mdi-alert" />
                <v-icon v-else icon="mdi-unfold-less-vertical" size="12" color="transparent" />
              </v-col>
              <v-col>
                <a :href="trendLink('lower')" target="_blank">
                  {{ lowerPrice }}
                </a>
              </v-col>
              <v-col style="text-align: center; padding-right: 2px">
                <v-icon v-if="lowerChangeIcon" class="mb-1" :icon="lowerChangeIcon" />
                <v-icon v-else class="mb-1" icon="mdi-unfold-less-vertical" color="transparent" />
              </v-col>
            </v-row>
          </v-sheet>
        </template>
        <span class="font-weight-bold">{{ name }} Lower {{ market }} price</span><br />
        <span v-if="flags('lower', market)">{{ flags('lower', market) }}<br /></span>
        <span>Current: {{ lowerPrice }}</span
        ><br />
        <span>Previous: {{ previousLowerPrice }}</span
        ><br />
        <span>Change: {{ deltaLowerPrice }} ({{ percentLowerChange.toFixed(2) }}%)</span>
      </v-tooltip>
    </v-col>
  </v-row>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import HeatMap from '@/heatMaps/heatMap';
import changeLogic from '@/components/changeLogic';
import { RegionPrices } from '@/repositories';
import { stringifyQuery } from 'vue-router';

export default defineComponent({
  name: 'RegionFcasRow',
  props: {
    market: { type: String, default: '' },
    name: { type: String, default: '' }, // region name
    prices: RegionPrices,
    raise: { type: Number, default: undefined },
    lower: { type: Number, default: undefined },
    previousRaise: { type: Number, default: undefined },
    previousLower: { type: Number, default: undefined }
  },
  computed: {
    raisePrice() {
      return `$${this.raise?.toFixed(2) ?? ' --'}`;
    },
    lowerPrice() {
      return `$${this.lower?.toFixed(2) ?? ' --'}`;
    },
    previousRaisePrice() {
      return `$${this.previousRaise?.toFixed(2) ?? ' --'}`;
    },
    previousLowerPrice() {
      return `$${this.previousLower?.toFixed(2) ?? ' --'}`;
    },
    deltaRaisePrice() {
      return `$${((this.raise ?? 0) - (this.previousRaise ?? 0)).toFixed(2)}`;
    },
    deltaLowerPrice() {
      return `$${((this.lower ?? 0) - (this.previousLower ?? 0)).toFixed(2)}`;
    },
    percentRaiseChange() {
      return changeLogic.percentChange(this.raise, this.previousRaise);
    },
    percentLowerChange() {
      return changeLogic.percentChange(this.lower, this.previousLower);
    },
    raiseColor() {
      return HeatMap.default.getColor(this.raise ?? 0, 0, 112).toRgba(0.25);
    },
    lowerColor() {
      return HeatMap.default.getColor(this.lower ?? 0, 0, 112).toRgba(0.25);
    },
    lowerChangeIcon() {
      return changeLogic.changeIcon(this.lower, this.previousLower);
    },
    raiseChangeIcon() {
      return changeLogic.changeIcon(this.raise, this.previousRaise);
    },
    marketFullName() {
      return this.market?.toLowerCase().replace('s', 'sec').replace('m', 'min');
    }
  },
  methods: {
    trendLink(raiseLower: string) {
      return `https://trends.global-roam.com/#/artifacts/F103D4E5-B351-4BD2-A615-DF40333D5F4E-${
        //
        this.name
      }1-${raiseLower}${this.marketFullName}?${stringifyQuery(this.$route.query)}`;
    },
    flags(raiseLower: string, mkt: string) {
      switch (raiseLower) {
        case 'raise':
          switch (mkt) {
            case 'Reg':
              return this.prices?.raiseRegFlags?.join(', ');
            case '1s':
              return this.prices?.raise1SecFlags?.join(', ');
            case '6s':
              return this.prices?.raise6SecFlags?.join(', ');
            case '60s':
              return this.prices?.raise60SecFlags?.join(', ');
            case '5m':
              return this.prices?.raise5MinFlags?.join(', ');
          }
          break;
        case 'lower':
          switch (mkt) {
            case 'Reg':
              return this.prices?.lowerRegFlags?.join(', ');
            case '1s':
              return this.prices?.lower1SecFlags?.join(', ');
            case '6s':
              return this.prices?.lower6SecFlags?.join(', ');
            case '60s':
              return this.prices?.lower60SecFlags?.join(', ');
            case '5m':
              return this.prices?.lower5MinFlags?.join(', ');
          }
          break;
      }
    }
  }
});
</script>

<style scoped>
a {
  color: black;
}
</style>
